import { useEffect, useCallback, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { IconLoader } from '@ui/src';
import { setIsAuthenticated } from '@auth/hoc/store/slice';
import { useLazyGetCurrentUserQuery } from '@auth/hoc/store/services';
import { useLogoutMutation } from '@administration/hoc/settings/profile/store/services';
import { useAppSelector, useAppDispatch } from '@store';

import cn from './styles.module.css';

const usePrivateRoutes = ({
  redirectLogin,
  userId,
  children,
}: {
  redirectLogin: string;
  userId: string | null;
  children: React.ReactNode;
}) => {
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const [isLoading, setIsLoading] = useState(true);

  const dispatch = useAppDispatch();

  const location = useLocation();

  const navigate = useNavigate();

  const [getCurrentUser] = useLazyGetCurrentUserQuery();
  const [logout] = useLogoutMutation();

  const getCurrentUserAction = useCallback(async () => {
    setIsLoading(true);

    const response = (await getCurrentUser()) as { error?: { status: number } };

    if (!response.error) {
      dispatch(setIsAuthenticated());

      setIsLoading(false);
    } else if (response.error) {
      if (response?.error?.status === 403 || response?.error?.status === 401) {
        const logoutResponse = await logout();

        if (logoutResponse) {
          localStorage.removeItem('activePermissions');
          localStorage.removeItem('userId');
          localStorage.removeItem('cardsEnabled');
          localStorage.removeItem('reportLink');

          navigate('/login');
        }
      }
    }
  }, []);

  useEffect(() => {
    if (userId && !user) {
      getCurrentUserAction();
    }
  }, [userId, getCurrentUserAction, user]);

  useEffect(() => {
    if (userId) {
      const redirectPath = localStorage.getItem('redirectPath');

      if (redirectPath) {
        navigate(redirectPath);

        setTimeout(() => {
          localStorage.removeItem('redirectPath');
        }, 5000);
      }
    }
  }, [userId]);

  let content;

  if (isLoading) {
    content = (
      <div className={cn.authLoader}>
        <IconLoader />
      </div>
    );

    if (!userId) {
      if (!location.pathname.includes('/login') && location.pathname !== '/') {
        localStorage.setItem(
          'redirectPath',
          `${location.pathname}${location.search || ''}`,
        );
      }

      content = <Navigate to={redirectLogin} />;
    }
  } else if (!isLoading && isAuthenticated) {
    content = children;
  } else {
    if (!location.pathname.includes('/login') && location.pathname !== '/') {
      localStorage.setItem(
        'redirectPath',
        `${location.pathname}${location.search || ''}`,
      );
    }

    content = <Navigate to={redirectLogin} />;
  }

  return content;
};

export default usePrivateRoutes;
